import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/MemberstackAuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getAvailability, updateAvailability, addBlockedTime, deleteBlockedTime, getBookings, updateBusinessSettings } from '../lib/api';
import memberstack from '../lib/memberstack';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Download, Sun, Moon, Monitor, Plus, Trash2, Clock, CalendarIcon, AlertTriangle, FileText, X, User, CreditCard, Lock } from 'lucide-react';
import { exportInvoices, exportPayments, exportTransactions } from '../lib/api';
import { format, parseISO, startOfDay, endOfDay, setHours, setMinutes } from 'date-fns';
import { cn } from '../lib/utils';

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return { value: `${hour}:00`, label: format(new Date().setHours(i, 0), 'h:mm a') };
});

export default function SettingsPage() {
  const { user, member, checkAuth } = useAuth();
  const { theme, setTheme } = useTheme();
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blockSheetOpen, setBlockSheetOpen] = useState(false);
  
  // New blocked time form
  const [newBlock, setNewBlock] = useState({
    name: '',
    block_type: 'recurring',
    start_time: '12:00',
    end_time: '13:00',
    days_of_week: [],
    start_date: null,
    end_date: null,
    is_all_day: false,
  });

  // Conflict warning dialog
  const [conflictWarningOpen, setConflictWarningOpen] = useState(false);
  const [conflictingBookings, setConflictingBookings] = useState([]);
  const [pendingBlockData, setPendingBlockData] = useState(null);

  // Statement fields
  const [businessName, setBusinessName] = useState('');
  const [statementAddress, setStatementAddress] = useState('');
  const [statementPhone, setStatementPhone] = useState('');
  const [statementGst, setStatementGst] = useState('');
  const [savingBiz, setSavingBiz] = useState(false);

  // Invoice settings
  const [invoiceType, setInvoiceType] = useState('invoice');
  const [gstRate, setGstRate] = useState(15);
  const [gstMode, setGstMode] = useState('none');
  const [defaultPaymentTerms, setDefaultPaymentTerms] = useState('on_receipt');
  const [customPaymentTermsDays, setCustomPaymentTermsDays] = useState(30);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [invoiceLogo, setInvoiceLogo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (user) {
      setBusinessName(user.business_name || '');
      setStatementAddress(user.statement_address || '');
      setStatementPhone(user.statement_phone || '');
      setStatementGst(user.statement_gst_number || '');
      // Invoice settings
      setInvoiceType(user.invoice_type || 'invoice');
      setGstRate(user.gst_rate ?? 15);
      setGstMode(user.gst_mode || 'none');
      setDefaultPaymentTerms(user.default_payment_terms || 'on_receipt');
      setCustomPaymentTermsDays(user.custom_payment_terms_days ?? 30);
      setInvoiceLogo(user.invoice_logo || null);
    }
  }, [user]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 500 * 1024) {
      toast.error('Image must be less than 500KB');
      return;
    }
    
    // Validate image dimensions
    const validateDimensions = () => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const { width, height } = img;
          // Check minimum dimensions
          if (width < 200 || height < 50) {
            reject(new Error(`Image too small (${width}x${height}). Minimum size is 200x50 pixels.`));
            return;
          }
          // Check maximum dimensions
          if (width > 800 || height > 400) {
            reject(new Error(`Image too large (${width}x${height}). Maximum size is 800x400 pixels.`));
            return;
          }
          // Check aspect ratio (should be roughly 2:1, allow some flexibility)
          const ratio = width / height;
          if (ratio < 1.5 || ratio > 4) {
            reject(new Error(`Image aspect ratio (${ratio.toFixed(1)}:1) is not ideal. Recommended: 2:1 ratio (e.g., 400x200 pixels).`));
            return;
          }
          resolve({ width, height });
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = URL.createObjectURL(file);
      });
    };
    
    try {
      await validateDimensions();
    } catch (err) {
      toast.error(err.message);
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/logo`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      setInvoiceLogo(data.logo_url);
      await checkAuth(); // Refresh user data
      toast.success('Logo uploaded successfully');
    } catch (err) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/logo`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Delete failed');
      
      setInvoiceLogo(null);
      await checkAuth();
      toast.success('Logo removed');
    } catch (err) {
      toast.error('Failed to remove logo');
    }
  };

  // Account deletion
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const handleRequestDeletion = async () => {
    if (deleteConfirmText.toUpperCase() !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setDeletingAccount(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/request-deletion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirm_text: deleteConfirmText })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Request failed');
      }
      
      const data = await response.json();
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
      toast.success(`Confirmation email sent to ${data.email}. Please check your inbox.`);
    } catch (err) {
      toast.error(err.message || 'Failed to request deletion');
    } finally {
      setDeletingAccount(false);
    }
  };

  // Password change
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const handleChangePassword = async () => {
    // Validate passwords
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    try {
      await memberstack.updateMemberAuth({
        oldPassword: currentPassword,
        newPassword: newPassword
      });
      
      setPasswordDialogOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed successfully');
    } catch (err) {
      console.error('Password change error:', err);
      // Memberstack errors
      if (err.message?.includes('incorrect') || err.message?.includes('wrong')) {
        toast.error('Current password is incorrect');
      } else if (err.message?.includes('weak') || err.message?.includes('short')) {
        toast.error('New password is too weak. Use at least 8 characters.');
      } else {
        toast.error(err.message || 'Failed to change password');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    try {
      const data = await getAvailability();
      setAvailability(data);
    } catch (err) {
      toast.error('Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const handleWorkingHoursChange = (day, field, value) => {
    setAvailability(prev => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [day]: {
          ...prev.working_hours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleSaveWorkingHours = async () => {
    setSaving(true);
    try {
      await updateAvailability({
        working_hours: availability.working_hours,
        blocked_times: availability.blocked_times || [],
      });
      toast.success('Working hours saved');
    } catch (err) {
      toast.error('Failed to save working hours');
    } finally {
      setSaving(false);
    }
  };

  // Check for booking conflicts before adding blocked time
  const checkForConflicts = async (blockData) => {
    try {
      const bookings = await getBookings({ status: 'scheduled' });
      const conflicts = [];
      
      if (blockData.block_type === 'one_time' && blockData.start_date && blockData.end_date) {
        const blockStart = new Date(blockData.start_date);
        const blockEnd = new Date(blockData.end_date);
        
        bookings.forEach(b => {
          const bStart = parseISO(b.start_at_utc);
          const bEnd = parseISO(b.end_at_utc);
          if (bStart < blockEnd && bEnd > blockStart) {
            conflicts.push(b);
          }
        });
      }
      
      return conflicts;
    } catch (err) {
      return [];
    }
  };

  const handleAddBlockedTime = async () => {
    if (!newBlock.name) {
      toast.error('Please enter a name for the blocked time');
      return;
    }
    
    const blockData = {
      name: newBlock.name,
      block_type: newBlock.block_type,
      is_all_day: newBlock.is_all_day,
    };
    
    if (newBlock.block_type === 'recurring') {
      blockData.start_time = newBlock.start_time;
      blockData.end_time = newBlock.end_time;
      blockData.days_of_week = newBlock.days_of_week;
    } else {
      blockData.start_date = newBlock.start_date?.toISOString();
      blockData.end_date = newBlock.end_date?.toISOString();
    }
    
    // Check for conflicts with existing bookings
    if (newBlock.block_type === 'one_time') {
      const conflicts = await checkForConflicts(blockData);
      if (conflicts.length > 0) {
        setConflictingBookings(conflicts);
        setPendingBlockData(blockData);
        setConflictWarningOpen(true);
        return;
      }
    }
    
    await submitBlockedTime(blockData);
  };

  const submitBlockedTime = async (blockData) => {
    try {
      await addBlockedTime(blockData);
      toast.success('Blocked time added');
      setBlockSheetOpen(false);
      setConflictWarningOpen(false);
      setPendingBlockData(null);
      setConflictingBookings([]);
      setNewBlock({
        name: '',
        block_type: 'recurring',
        start_time: '12:00',
        end_time: '13:00',
        days_of_week: [],
        start_date: null,
        end_date: null,
        is_all_day: false,
      });
      fetchAvailability();
    } catch (err) {
      toast.error('Failed to add blocked time');
    }
  };

  const handleDeleteBlockedTime = async (id) => {
    try {
      await deleteBlockedTime(id);
      toast.success('Blocked time removed');
      fetchAvailability();
    } catch (err) {
      toast.error('Failed to remove blocked time');
    }
  };

  const handleExport = (type) => {
    let url;
    switch (type) {
      case 'invoices':
        url = exportInvoices();
        break;
      case 'payments':
        url = exportPayments();
        break;
      case 'transactions':
        url = exportTransactions();
        break;
      default:
        return;
    }
    window.open(url, '_blank');
  };

  const toggleDayOfWeek = (dayIndex) => {
    setNewBlock(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(dayIndex)
        ? prev.days_of_week.filter(d => d !== dayIndex)
        : [...prev.days_of_week, dayIndex],
    }));
  };

  return (
    <div className="flex flex-col h-full" data-testid="settings-page">
      {/* Header */}
      <div className="px-8 py-4 border-b border-border bg-background/95 backdrop-blur-sm">
        <h1 className="text-2xl font-heading font-bold">Settings</h1>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-3xl space-y-6">
          {/* Business Info */}
          <Card>
            <CardHeader>
              <CardTitle>Business Information</CardTitle>
              <CardDescription>Your business details — these appear on printed statements and exports</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Business Name</Label>
                  <Input 
                    value={businessName} 
                    onChange={(e) => setBusinessName(e.target.value)} 
                    placeholder="Your Business Name" 
                    data-testid="business-name-input" 
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground text-xs">Email</Label>
                    <p className="font-medium mt-1 text-sm">{user?.email}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Timezone</Label>
                    <p className="font-medium mt-1 text-sm">{user?.timezone}</p>
                  </div>
                </div>
              </div>
              <Separator />
              <p className="text-sm font-medium">Statement / Export Fields</p>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Address</Label>
                  <Input value={statementAddress} onChange={(e) => setStatementAddress(e.target.value)} placeholder="123 Main St, Auckland 1010" data-testid="statement-address" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Phone</Label>
                    <Input value={statementPhone} onChange={(e) => setStatementPhone(e.target.value)} placeholder="+64 9 123 4567" data-testid="statement-phone" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">GST Number</Label>
                    <Input value={statementGst} onChange={(e) => setStatementGst(e.target.value)} placeholder="123-456-789" data-testid="statement-gst" />
                  </div>
                </div>
                <Button size="sm" disabled={savingBiz} onClick={async () => {
                  setSavingBiz(true);
                  try {
                    await updateBusinessSettings({
                      business_name: businessName || null,
                      statement_address: statementAddress || null,
                      statement_phone: statementPhone || null,
                      statement_gst_number: statementGst || null,
                    });
                    // Refresh user data to update the sidebar and other places
                    await checkAuth();
                    toast.success('Business settings saved');
                  } catch { toast.error('Failed to save'); }
                  finally { setSavingBiz(false); }
                }} data-testid="save-business-settings">{savingBiz ? 'Saving...' : 'Save Business Settings'}</Button>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Invoice Settings
              </CardTitle>
              <CardDescription>Configure how invoices are generated and displayed</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {/* Logo Upload */}
                <div className="space-y-2">
                  <Label className="text-xs">Business Logo</Label>
                  <div className="flex items-center gap-4">
                    {invoiceLogo ? (
                      <div className="relative">
                        <img src={invoiceLogo} alt="Logo" className="h-16 w-auto max-w-[200px] object-contain border rounded-lg p-2 bg-white" />
                        <Button 
                          variant="destructive" 
                          size="icon" 
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                          onClick={handleLogoDelete}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="h-16 w-32 border-2 border-dashed rounded-lg flex items-center justify-center text-muted-foreground text-xs">
                        No logo
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="logo-upload"
                        disabled={uploadingLogo}
                      />
                      <label htmlFor="logo-upload">
                        <Button variant="outline" size="sm" asChild disabled={uploadingLogo}>
                          <span>{uploadingLogo ? 'Uploading...' : invoiceLogo ? 'Change Logo' : 'Upload Logo'}</span>
                        </Button>
                      </label>
                      <p className="text-xs text-muted-foreground mt-1">
                        <strong>Recommended:</strong> 400×200 pixels (2:1 ratio)
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        PNG or JPG • Min: 200×50 • Max: 800×400 • Under 500KB
                      </p>
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-1">
                  <Label className="text-xs">Invoice Type</Label>
                  <Select value={invoiceType} onValueChange={setInvoiceType}>
                    <SelectTrigger data-testid="invoice-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="invoice">Invoice</SelectItem>
                      <SelectItem value="tax_invoice">Tax Invoice (GST Registered)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {invoiceType === 'tax_invoice' ? 'Shows "TAX INVOICE" header and GST breakdown' : 'Standard invoice without GST details'}
                  </p>
                </div>
                
                {invoiceType === 'tax_invoice' && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">GST Rate (%)</Label>
                        <Input 
                          type="number" 
                          step="0.1" 
                          min="0" 
                          max="100"
                          value={gstRate} 
                          onChange={(e) => setGstRate(parseFloat(e.target.value) || 0)} 
                          data-testid="gst-rate-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">GST Mode</Label>
                        <Select value={gstMode} onValueChange={setGstMode}>
                          <SelectTrigger data-testid="gst-mode-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inclusive">Inclusive (prices include GST)</SelectItem>
                            <SelectItem value="exclusive">Exclusive (GST added on top)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {gstMode === 'inclusive' 
                        ? `Service prices include ${gstRate}% GST — invoice shows breakdown`
                        : `${gstRate}% GST will be added on top of service prices`}
                    </p>
                  </>
                )}

                <Separator />

                <div className="space-y-1">
                  <Label className="text-xs">Default Payment Terms</Label>
                  <Select value={defaultPaymentTerms} onValueChange={setDefaultPaymentTerms}>
                    <SelectTrigger data-testid="payment-terms-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="on_receipt">Due on Receipt</SelectItem>
                      <SelectItem value="7">Net 7 (Due in 7 days)</SelectItem>
                      <SelectItem value="14">Net 14 (Due in 14 days)</SelectItem>
                      <SelectItem value="30">Net 30 (Due in 30 days)</SelectItem>
                      <SelectItem value="60">Net 60 (Due in 60 days)</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {defaultPaymentTerms === 'custom' && (
                  <div className="space-y-1">
                    <Label className="text-xs">Custom Days</Label>
                    <Input 
                      type="number" 
                      min="1" 
                      max="365"
                      value={customPaymentTermsDays} 
                      onChange={(e) => setCustomPaymentTermsDays(parseInt(e.target.value) || 30)} 
                      data-testid="custom-payment-days-input"
                    />
                  </div>
                )}

                <Button size="sm" disabled={savingInvoice} onClick={async () => {
                  setSavingInvoice(true);
                  try {
                    await updateBusinessSettings({
                      invoice_type: invoiceType,
                      gst_rate: invoiceType === 'tax_invoice' ? gstRate : null,
                      gst_mode: invoiceType === 'tax_invoice' ? gstMode : 'none',
                      default_payment_terms: defaultPaymentTerms,
                      custom_payment_terms_days: defaultPaymentTerms === 'custom' ? customPaymentTermsDays : null,
                    });
                    await checkAuth();
                    toast.success('Invoice settings saved');
                  } catch { toast.error('Failed to save'); }
                  finally { setSavingInvoice(false); }
                }} data-testid="save-invoice-settings">{savingInvoice ? 'Saving...' : 'Save Invoice Settings'}</Button>
              </div>
            </CardContent>
          </Card>

          {/* Working Hours */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Working Hours
              </CardTitle>
              <CardDescription>Set your standard availability for each day</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <p className="text-muted-foreground">Loading...</p>
              ) : (
                <>
                  {DAYS.map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-4">
                      <div className="w-28">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={availability?.working_hours?.[key]?.enabled ?? true}
                            onCheckedChange={(checked) => handleWorkingHoursChange(key, 'enabled', checked)}
                            data-testid={`working-hours-${key}-enabled`}
                          />
                          <Label className="text-sm">{label}</Label>
                        </div>
                      </div>
                      {availability?.working_hours?.[key]?.enabled && (
                        <div className="flex items-center gap-2 flex-1">
                          <Select
                            value={availability?.working_hours?.[key]?.start || '09:00'}
                            onValueChange={(v) => handleWorkingHoursChange(key, 'start', v)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TIME_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="text-muted-foreground">to</span>
                          <Select
                            value={availability?.working_hours?.[key]?.end || '17:00'}
                            onValueChange={(v) => handleWorkingHoursChange(key, 'end', v)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TIME_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {!availability?.working_hours?.[key]?.enabled && (
                        <span className="text-sm text-muted-foreground">Closed</span>
                      )}
                    </div>
                  ))}
                  <Button onClick={handleSaveWorkingHours} disabled={saving} className="mt-4" data-testid="save-working-hours">
                    {saving ? 'Saving...' : 'Save Working Hours'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Blocked Times */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Blocked Times</CardTitle>
                  <CardDescription>Block out lunch breaks, holidays, and other unavailable times</CardDescription>
                </div>
                <Sheet open={blockSheetOpen} onOpenChange={setBlockSheetOpen}>
                  <SheetTrigger asChild>
                    <Button size="sm" data-testid="add-blocked-time-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add Block
                    </Button>
                  </SheetTrigger>
                  <SheetContent>
                    <SheetHeader>
                      <SheetTitle>Add Blocked Time</SheetTitle>
                    </SheetHeader>
                    <div className="space-y-4 mt-6">
                      <div className="space-y-2">
                        <Label>Name</Label>
                        <Input
                          value={newBlock.name}
                          onChange={(e) => setNewBlock(p => ({ ...p, name: e.target.value }))}
                          placeholder="e.g., Lunch Break, Christmas"
                          data-testid="block-name-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Type</Label>
                        <Select
                          value={newBlock.block_type}
                          onValueChange={(v) => setNewBlock(p => ({ ...p, block_type: v }))}
                        >
                          <SelectTrigger data-testid="block-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="recurring">Recurring (e.g., daily lunch)</SelectItem>
                            <SelectItem value="one_time">One-time (e.g., holiday)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {newBlock.block_type === 'recurring' ? (
                        <>
                          <div className="space-y-2">
                            <Label>Days</Label>
                            <div className="flex flex-wrap gap-2">
                              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                                <Button
                                  key={idx}
                                  type="button"
                                  variant={newBlock.days_of_week.includes(idx) ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => toggleDayOfWeek(idx)}
                                  data-testid={`block-day-${idx}`}
                                >
                                  {day}
                                </Button>
                              ))}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Start Time</Label>
                              <Select
                                value={newBlock.start_time}
                                onValueChange={(v) => setNewBlock(p => ({ ...p, start_time: v }))}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {TIME_OPTIONS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>End Time</Label>
                              <Select
                                value={newBlock.end_time}
                                onValueChange={(v) => setNewBlock(p => ({ ...p, end_time: v }))}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {TIME_OPTIONS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="all-day"
                              checked={newBlock.is_all_day}
                              onCheckedChange={(v) => setNewBlock(p => ({ ...p, is_all_day: v }))}
                            />
                            <Label htmlFor="all-day">All day</Label>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Start Date</Label>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" className={cn("w-full justify-start", !newBlock.start_date && "text-muted-foreground")}>
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {newBlock.start_date ? format(newBlock.start_date, 'PPP') : 'Pick date'}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                  <Calendar
                                    mode="single"
                                    selected={newBlock.start_date}
                                    onSelect={(d) => setNewBlock(p => ({ ...p, start_date: d }))}
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>
                            <div className="space-y-2">
                              <Label>End Date</Label>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" className={cn("w-full justify-start", !newBlock.end_date && "text-muted-foreground")}>
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {newBlock.end_date ? format(newBlock.end_date, 'PPP') : 'Pick date'}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                  <Calendar
                                    mode="single"
                                    selected={newBlock.end_date}
                                    onSelect={(d) => setNewBlock(p => ({ ...p, end_date: d }))}
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                        </>
                      )}
                      
                      <Button onClick={handleAddBlockedTime} className="w-full" data-testid="save-blocked-time">
                        Add Blocked Time
                      </Button>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </CardHeader>
            <CardContent>
              {!availability?.blocked_times?.length ? (
                <p className="text-sm text-muted-foreground">No blocked times configured</p>
              ) : (
                <div className="space-y-2">
                  {availability.blocked_times.map((bt) => (
                    <div key={bt.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">{bt.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {bt.block_type === 'recurring' ? (
                            <>
                              {bt.days_of_week?.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')}
                              {' • '}
                              {bt.start_time} - {bt.end_time}
                            </>
                          ) : (
                            <>
                              {bt.start_date && format(new Date(bt.start_date), 'MMM d')}
                              {bt.end_date && ` - ${format(new Date(bt.end_date), 'MMM d, yyyy')}`}
                              {bt.is_all_day && ' (All day)'}
                            </>
                          )}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteBlockedTime(bt.id)}
                        data-testid={`delete-block-${bt.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Appearance */}
          <Card>
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>Customize how Bõkr looks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Theme</Label>
                  <p className="text-sm text-muted-foreground">Select your preferred theme</p>
                </div>
                <Select value={theme} onValueChange={setTheme}>
                  <SelectTrigger className="w-40" data-testid="theme-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">
                      <div className="flex items-center gap-2">
                        <Sun className="w-4 h-4" /> Light
                      </div>
                    </SelectItem>
                    <SelectItem value="dark">
                      <div className="flex items-center gap-2">
                        <Moon className="w-4 h-4" /> Dark
                      </div>
                    </SelectItem>
                    <SelectItem value="system">
                      <div className="flex items-center gap-2">
                        <Monitor className="w-4 h-4" /> System
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Data Export */}
          <Card>
            <CardHeader>
              <CardTitle>Data Export</CardTitle>
              <CardDescription>Download your data as CSV files</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium">Invoices</p>
                  <p className="text-sm text-muted-foreground">All invoices with status and amounts</p>
                </div>
                <Button variant="outline" onClick={() => handleExport('invoices')} data-testid="export-invoices">
                  <Download className="w-4 h-4 mr-2" /> Export
                </Button>
              </div>
              <Separator />
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium">Payments</p>
                  <p className="text-sm text-muted-foreground">All recorded payments</p>
                </div>
                <Button variant="outline" onClick={() => handleExport('payments')} data-testid="export-payments">
                  <Download className="w-4 h-4 mr-2" /> Export
                </Button>
              </div>
              <Separator />
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium">Transactions</p>
                  <p className="text-sm text-muted-foreground">Complete ledger history</p>
                </div>
                <Button variant="outline" onClick={() => handleExport('transactions')} data-testid="export-transactions">
                  <Download className="w-4 h-4 mr-2" /> Export
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Account & Subscription */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Account & Subscription
              </CardTitle>
              <CardDescription>Your Bõkr account details and plan information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Member Details */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Account</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{member?.auth?.email || user?.email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Member ID</p>
                    <p className="font-mono text-xs">{member?.id?.slice(0, 16) || '-'}...</p>
                  </div>
                </div>
              </div>

              {/* Password */}
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm">Password</p>
                    <p className="text-xs text-muted-foreground">Last changed: Unknown</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setPasswordDialogOpen(true)}>
                  Change Password
                </Button>
              </div>

              <Separator />

              {/* Plan Details */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Subscription</Label>
                {member?.planConnections && member.planConnections.length > 0 ? (
                  <div className="space-y-3">
                    {member.planConnections.map((plan, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-primary" />
                            <span className="font-semibold">{plan.planName || plan.planId || 'Active Plan'}</span>
                            <Badge variant="default" className="text-xs">Active</Badge>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                            {plan.createdAt && (
                              <p>Started: {format(new Date(plan.createdAt), 'MMM d, yyyy')}</p>
                            )}
                            {plan.currentPeriodEnd && (
                              <p>Renews: {format(new Date(plan.currentPeriodEnd), 'MMM d, yyyy')}</p>
                            )}
                            {plan.status && (
                              <p>Status: <span className="capitalize">{plan.status}</span></p>
                            )}
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => window.open('https://getbokr.com/account', '_blank')}>
                          Manage
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-muted/30 rounded-lg text-center">
                    <p className="text-muted-foreground">No active subscription</p>
                    <Button variant="default" size="sm" className="mt-2" onClick={() => window.location.href = '/subscribe'}>
                      Subscribe Now
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="text-destructive flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Danger Zone
              </CardTitle>
              <CardDescription>Irreversible actions that affect your account</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-destructive/5 rounded-lg border border-destructive/20">
                <div>
                  <p className="font-medium">Delete Account</p>
                  <p className="text-sm text-muted-foreground">Permanently delete your account and all data. This cannot be undone.</p>
                </div>
                <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
                  Delete Account
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Your Account
            </DialogTitle>
            <DialogDescription>
              This action is permanent and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 space-y-2">
              <p className="font-semibold text-destructive">Warning: All of the following will be permanently deleted:</p>
              <ul className="text-sm space-y-1 ml-4 list-disc">
                <li>Your account and login credentials</li>
                <li>All clients and their contact information</li>
                <li>All bookings (past, present, and future)</li>
                <li>All invoices and payment records</li>
                <li>All services and pricing</li>
                <li>All settings and customizations</li>
              </ul>
              <p className="text-sm font-medium text-destructive mt-3">Your subscription will also be cancelled.</p>
            </div>
            
            <div className="space-y-2">
              <Label>Type <span className="font-mono font-bold">DELETE</span> to confirm:</Label>
              <Input 
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE here"
                className="font-mono"
              />
            </div>
            
            <p className="text-xs text-muted-foreground">
              A confirmation email will be sent to your registered email address. 
              You must click the link in that email to complete the deletion. 
              The link expires in 24 hours.
            </p>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmText(''); }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRequestDeletion}
              disabled={deleteConfirmText.toUpperCase() !== 'DELETE' || deletingAccount}
            >
              {deletingAccount ? 'Sending...' : 'Send Deletion Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={(open) => {
        setPasswordDialogOpen(open);
        if (!open) {
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Change Password
            </DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input 
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
            </div>
            
            <Separator />
            
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input 
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
              <p className="text-xs text-muted-foreground">Minimum 8 characters</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input 
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-destructive">Passwords do not match</p>
              )}
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleChangePassword}
              disabled={!currentPassword || newPassword.length < 8 || newPassword !== confirmPassword || changingPassword}
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conflict Warning Dialog */}
      <Dialog open={conflictWarningOpen} onOpenChange={setConflictWarningOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Existing Bookings Found
            </DialogTitle>
            <DialogDescription>
              You have {conflictingBookings.length} booking(s) during this blocked time period. These clients may need to be rescheduled.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {conflictingBookings.map(booking => (
              <div key={booking.id} className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{booking.client_name}</p>
                <p className="text-sm text-muted-foreground">
                  {booking.service_name} • {format(parseISO(booking.start_at_utc), 'PPP p')}
                </p>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setConflictWarningOpen(false);
              setPendingBlockData(null);
              setConflictingBookings([]);
            }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => {
              if (pendingBlockData) submitBlockedTime(pendingBlockData);
            }}>
              Block Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
